import { useCallback, useMemo } from 'react'
import { useApolloClient, useMutation, useQuery } from '@apollo/client/react'

import { DISMISS_BANNER } from '../graphql/account'
import { graphql } from '../../gql'
import { useUser } from '../context/User'

export const BannerTypes = {
  channelGroupsAnnouncement: 'channelGroupsAnnouncement',
}

export const GetDismissedBanners = graphql(/* GraphQL */ `
  query GetDismissedBanners {
    account {
      id
      dismissedBanners
    }
  }
`)

type UseDismissBannerReturn = {
  onDismissBanner: (overrideBanner?: string) => void
  isActive: boolean
}

/**
 * Hook for dismissable banners within app-shell.
 *
 * NOTE: A similar hook exists in publish-frontend at `services/publish/publish-frontend/src/hooks/useDismissableBanner.ts`.
 * That hook has additional features like tracking support and account signup date filtering.
 * This hook is for internal app-shell use only and is not exported from the public API.
 */
const useDismissBanner = ({
  banner,
}: {
  banner: string
}): UseDismissBannerReturn => {
  const user = useUser()
  const apolloClient = useApolloClient()
  const { data, refetch } = useQuery(GetDismissedBanners, {
    fetchPolicy: 'cache-first',
  })
  const [dismissBanner] = useMutation<{
    dismissBanner: { __typename: string }
  }>(DISMISS_BANNER, {
    variables: { banner },
    update: (cache) => {
      if (!data?.account) {
        return
      }

      const account = data.account

      // optimistic response
      cache.writeQuery({
        query: GetDismissedBanners,
        data: {
          account: {
            ...account,
            dismissedBanners: [...(account?.dismissedBanners ?? []), banner],
          },
        },
      })
    },
  })
  const dismissedBanners = data?.account?.dismissedBanners
  const isActive = !!dismissedBanners && !dismissedBanners.includes(banner)

  const onDismissBanner = useCallback(
    async (overrideBanner?: string): Promise<void> => {
      const bannerToDismiss =
        typeof overrideBanner === 'string' ? overrideBanner : banner
      if (!bannerToDismiss) {
        return
      }

      if (user.isImpersonation) {
        // During impersonation, update the local cache only — do NOT persist to the customer's account
        const currentData = apolloClient.cache.readQuery({
          query: GetDismissedBanners,
        })
        if (currentData?.account) {
          apolloClient.cache.writeQuery({
            query: GetDismissedBanners,
            data: {
              account: {
                ...currentData.account,
                dismissedBanners: [
                  ...(currentData.account.dismissedBanners ?? []),
                  bannerToDismiss,
                ],
              },
            },
          })
        }
        return
      }

      const response = await dismissBanner({
        variables: { banner: bannerToDismiss },
      })

      const isSuccess =
        response.data?.dismissBanner.__typename === 'DismissBannerResponse'

      if (!isSuccess) {
        // refetch to get the latest dismissed banners after unsuccessful response shape
        await refetch()
      }
    },
    [dismissBanner, refetch, banner, user.isImpersonation, apolloClient],
  )

  // memoize the banner object to avoid unnecessary changes
  // when used in other hooks/components
  return useMemo(
    () => ({ onDismissBanner, isActive }),
    [isActive, onDismissBanner],
  )
}

export default useDismissBanner
